import { useRef, useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { apiClient, normalizeApiError } from '../../lib/apiClient'

export default function ProfilePhoto({ endpoint, photoUrl, onSaved }: { endpoint: string; photoUrl?: string | null; onSaved: () => unknown }) {
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState('')
  const input = useRef<HTMLInputElement>(null)
  const mutation = useMutation({
    mutationFn: async (remove: boolean) => {
      if (remove) return apiClient.delete(endpoint)
      const data = new FormData()
      if (file) data.append('photo', file)
      return apiClient.post(endpoint, data)
    },
    onSuccess: () => { setFile(null); if (input.current) input.current.value = ''; onSaved(); window.dispatchEvent(new Event('auth:profile-updated')) },
  })
  function submit(event: FormEvent) {
    event.preventDefault()
    if (!file || !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 2 * 1024 * 1024) {
      setError('Select a JPG, PNG or WebP image, maximum 2 MB.'); return
    }
    setError(''); mutation.mutate(false)
  }
  const uploadError = mutation.isError ? normalizeApiError(mutation.error) : null
  const uploadErrorMessage = uploadError
    ? Object.values(uploadError.validation).flat()[0] ?? uploadError.message
    : ''
  return <section className="mb-4 border rounded p-3" aria-label="Profile photo">
    {photoUrl ? <img src={photoUrl} alt="Profile" width={100} height={100} style={{ objectFit: 'cover', borderRadius: '50%' }} /> : <p>No profile photo uploaded.</p>}
    <form onSubmit={submit}>
      <label className="d-block my-2">Profile photo (JPG, PNG, WebP; up to 2 MB)
        <input ref={input} className="form-control" type="file" accept="image/jpeg,image/png,image/webp" onChange={event => { setFile(event.target.files?.[0] ?? null); setError(''); mutation.reset() }} />
      </label>
      <button className="btn btn-outline-primary" disabled={mutation.isPending || !file}>{mutation.isPending ? 'Saving…' : 'Upload photo'}</button>
      {photoUrl && <button type="button" className="btn btn-outline-danger ms-2" disabled={mutation.isPending} onClick={() => { if (window.confirm('Remove this profile photo?')) mutation.mutate(true) }}>Remove photo</button>}
    </form>
    {error && <p role="alert">{error}</p>}
    {mutation.isError && <p role="alert">{uploadErrorMessage}</p>}
    {mutation.isSuccess && <p role="status">Photo updated.</p>}
  </section>
}
